import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { IDENTITY_SERVICE_BASE_URL } from "constants";

const API_BASE_URL = `${IDENTITY_SERVICE_BASE_URL}/api/v1/privileges`;

export default function usePrivileges() {
  const authToken = sessionStorage.getItem("authToken");
  const [privileges, setPrivileges] = useState([]);
  const [loading, setLoading] = useState(false);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  const fetchAllPrivileges = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);

    try {
      const res = await axios.get(API_BASE_URL, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      setPrivileges(res.data);
    } catch (error) {
      handleAxiosErrors(error, "fetch all privileges");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // add privilege
  const addPrivilege = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const res = await axios.post(API_BASE_URL, payload, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        });

        setPrivileges((prev) => [res.data, ...prev]);
        toast.success("Privilege created successfully");
      } catch (error) {
        handleAxiosErrors(error, "add privilege");
      }
    },
    [authToken]
  );

  // update privilege
  const updatePrivilege = useCallback(
    async (payload, privilegeId) => {
      if (!authToken) return;
      try {
        const res = await axios.put(`${API_BASE_URL}/${privilegeId}`, payload, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        });

        setPrivileges((prev) =>
          prev.map((privilege) =>
            privilege.id === privilegeId ? res.data : privilege
          )
        );
        toast.success("Privilege updated successfully");
      } catch (error) {
        handleAxiosErrors(error, "update privilege");
      }
    },
    [authToken]
  );

  // delete privilege
  const deletePrivilege = useCallback(
    async (privilegeId) => {
      if (!authToken) return;
      try {
        await axios.delete(`${API_BASE_URL}/${privilegeId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        setPrivileges((prev) =>
          prev.filter((privilege) => privilege.id !== privilegeId)
        );
        toast.success("Privilege deleted successfully");
      } catch (error) {
        handleAxiosErrors(error, "delete privilege");
      }
    },
    [authToken]
  );

  // get user privileges
  const fetchUserPrivileges = useCallback(
    async (userId) => {
      if (!authToken) return;
      try {
        const data = await axios.get(`${API_BASE_URL}/user/${userId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        return data;
      } catch (error) {
        handleAxiosErrors(error, "fetch user privileges");
      }
    },
    [authToken]
  );

  // assign privileges to user
  const assignPrivilegesToUser = useCallback(
    async (userId, payload) => {
      if (!authToken) return;
      try {
        const res = await axios.put(
          `${API_BASE_URL}/user/${userId}/assign`,
          payload,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        toast.success("Privileges assigned successfully");
        return res;
      } catch (error) {
        handleAxiosErrors(error, "assign privileges to user");
      }
    },
    [authToken]
  );

  // remove privileges from user
  const removePrivilegesFromUser = useCallback(
    async (userId, privilegeIds = []) => {
      if (!authToken) return;
      try {
        // make one DELETE request per privilege ID
        await Promise.all(
          privilegeIds.map((privilegeId) =>
            axios.delete(
              `${API_BASE_URL}/user/${userId}/remove/${privilegeId}`,
              {
                headers: { Authorization: `Bearer ${authToken}` },
              }
            )
          )
        );

        // toast.success("Privileges removed successfully");
      } catch (error) {
        handleAxiosErrors(error, "remove privileges from user");
      }
    },
    [authToken]
  );

  // fetch automatically
  useEffect(() => {
    if (authToken) fetchAllPrivileges();
  }, [authToken, fetchAllPrivileges]);

  return {
    privileges,
    addPrivilege,
    updatePrivilege,
    deletePrivilege,
    loading,
    fetchUserPrivileges,
    assignPrivilegesToUser,
    removePrivilegesFromUser,
  };
}
